import { Alert, Select, Spin } from 'antd'
import { useRolodexEntries } from 'features/rolodex'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { GeckoComponent, RolodexEntryType } from 'types'

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.5rem;
`

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    background-color: ${({ theme }) => theme.forecast.background2} !important;
    border: 1px solid ${({ theme }) => theme.forecast.background4} !important;
    color: ${({ theme }) => theme.forecast.activeText} !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

interface ComponentSelectorProps {
  siteId: string | undefined
  selectedAssetId: string | undefined
  selectedComponentId: string | undefined
  onAssetChange: (assetId: string) => void
  onComponentChange: (componentId: string) => void
}

export const ComponentSelector: React.FC<ComponentSelectorProps> = ({
  siteId,
  selectedAssetId,
  selectedComponentId,
  onAssetChange,
  onComponentChange,
}) => {
  const { units, components, isLoading } = useRolodexEntries(
    siteId
      ? {
          type: [RolodexEntryType.UNIT, RolodexEntryType.COMPONENT],
          tags: { [RolodexEntryType.SITE]: siteId },
          limit: 100000,
        }
      : []
  )

  const assetOptions = useMemo(() => {
    return units.map((unit) => ({
      label: unit.data.name || unit.id,
      value: unit.id,
    }))
  }, [units])

  const componentOptions = useMemo(() => {
    if (!selectedAssetId) return []

    const filteredComponents = components.filter(
      (component) => component.tags[RolodexEntryType.UNIT] === selectedAssetId
    )

    return filteredComponents.map((component: GeckoComponent) => ({
      label: component.data.name || component.id,
      value: component.id,
    }))
  }, [components, selectedAssetId])

  if (isLoading) {
    return (
      <SelectorContainer>
        <Spin tip='Loading assets...' />
      </SelectorContainer>
    )
  }

  if (!siteId) {
    return (
      <SelectorContainer>
        <Alert
          title='No Site Selected'
          description='Please select a site to continue.'
          type='warning'
          showIcon
        />
      </SelectorContainer>
    )
  }

  return (
    <SelectorContainer>
      <div>
        <Label>Select Asset</Label>
        <StyledSelect
          showSearch
          placeholder='Select an asset...'
          value={selectedAssetId}
          onChange={(value) => onAssetChange(value as string)}
          options={assetOptions}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>

      {selectedAssetId && (
        <div>
          <Label>Select Component</Label>
          <StyledSelect
            showSearch
            placeholder='Select a component...'
            value={selectedComponentId}
            onChange={(value) => onComponentChange(value as string)}
            options={componentOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      )}
    </SelectorContainer>
  )
}
